import { useState, useEffect, useCallback } from 'react';
import { checkRateLimit } from '../utils/authValidation';

export const useAuthSecurity = () => {
  const [securityState, setSecurityState] = useState({
    isRateLimited: false,
    remainingAttempts: 5,
    lockoutTime: 0,
    suspiciousActivity: false,
    deviceFingerprint: null,
  });

  // Generate device fingerprint for security
  useEffect(() => {
    const generateFingerprint = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      
      const fingerprint = {
        screen: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        userAgent: navigator.userAgent.slice(0, 100), // Truncated for privacy
        canvas: canvas.toDataURL().slice(0, 50), // Truncated
        timestamp: Date.now()
      };
      
      setSecurityState(prev => ({
        ...prev,
        deviceFingerprint: btoa(JSON.stringify(fingerprint)).slice(0, 32)
      }));
    };

    generateFingerprint();
  }, []);

  // Check rate limiting
  const checkSubmissionLimit = useCallback((identifier, action = 'auth') => {
    const result = checkRateLimit(`${action}_${identifier}`, 5, 300000); // 5 attempts per 5 minutes
    
    setSecurityState(prev => ({
      ...prev,
      isRateLimited: !result.allowed,
      lockoutTime: result.waitTime,
      remainingAttempts: result.allowed ? prev.remainingAttempts - 1 : 0
    }));
    
    return result;
  }, []);

  // Detect suspicious patterns
  const detectSuspiciousActivity = useCallback((attempts, timeWindow = 60000) => {
    const now = Date.now();
    const recentAttempts = attempts.filter(attempt => now - attempt.timestamp < timeWindow);
    
    const suspicious = 
      recentAttempts.length > 10 || // Too many attempts
      recentAttempts.some(attempt => attempt.userAgent !== navigator.userAgent) || // Different user agent
      recentAttempts.filter(attempt => attempt.success === false).length > 5; // Too many failures
    
    setSecurityState(prev => ({
      ...prev,
      suspiciousActivity: suspicious
    }));
    
    return suspicious;
  }, []);

  // Security recommendations based on state
  const getSecurityRecommendations = useCallback(() => {
    const recommendations = [];
    
    if (securityState.isRateLimited) {
      recommendations.push({
        type: 'warning',
        message: 'Too many attempts. Please wait before trying again.',
        action: 'Wait and try again later'
      });
    }
    
    if (securityState.suspiciousActivity) {
      recommendations.push({
        type: 'error',
        message: 'Suspicious activity detected on your account.',
        action: 'Change your password immediately'
      });
    }
    
    // General security tips
    recommendations.push({
      type: 'info',
      message: 'Use a strong, unique password for better security.',
      action: 'Consider using a password manager'
    });
    
    return recommendations;
  }, [securityState]);

  // Log security events (in production, send to monitoring service)
  const logSecurityEvent = useCallback((event, details = {}) => {
    const securityLog = {
      timestamp: new Date().toISOString(),
      event,
      details: {
        ...details,
        deviceFingerprint: securityState.deviceFingerprint,
        userAgent: navigator.userAgent.slice(0, 100),
        ip: 'client-side', // Would be filled by server
      }
    };
    
    // In production, send to security monitoring service
    console.log('Security Event:', securityLog);
    
    // Store locally for pattern detection
    const existingLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
    existingLogs.push(securityLog);
    
    // Keep only last 50 events
    const recentLogs = existingLogs.slice(-50);
    localStorage.setItem('security_logs', JSON.stringify(recentLogs));
  }, [securityState.deviceFingerprint]);

  // Clear security state
  const clearSecurityState = useCallback(() => {
    setSecurityState({
      isRateLimited: false,
      remainingAttempts: 5,
      lockoutTime: 0,
      suspiciousActivity: false,
      deviceFingerprint: securityState.deviceFingerprint, // Keep fingerprint
    });
  }, [securityState.deviceFingerprint]);

  // Validate session integrity
  const validateSession = useCallback(() => {
    const sessionStart = sessionStorage.getItem('session_start');
    const maxSessionTime = 24 * 60 * 60 * 1000; // 24 hours
    
    if (sessionStart && Date.now() - parseInt(sessionStart) > maxSessionTime) {
      logSecurityEvent('session_expired');
      return false;
    }
    
    return true;
  }, [logSecurityEvent]);

  return {
    securityState,
    checkSubmissionLimit,
    detectSuspiciousActivity,
    getSecurityRecommendations,
    logSecurityEvent,
    clearSecurityState,
    validateSession,
  };
};